import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';
import {
  _useKanbanColumnsQuery,
  _readCustomColumns,
  _readLabelOverrides,
  _customColumnsCache,
  useColumnLabels,
} from './use-column-labels';

/**
 * Resolve o label efetivo de uma coluna VINDA DO BANCO. Diferente do
 * `getLabel` global (que cai pra defaults code-side se não há override),
 * este resolve a precedência canônica das colunas persistidas:
 *
 *   1. label override do admin (`kanban_column_labels`)
 *   2. label do próprio row (`kanban_custom_columns.label`) — sempre presente
 *
 * Necessário pós-Migration 045 quando slug do defaults coincide com slug do
 * banco. Antes da migration, slugs eram disjuntos e o bug ficava latente
 * (getLabel sempre retornava o label dos defaults).
 */
function labelForBackendRow(overrides: Record<string, string>, row: { slug: string; label: string }): string {
  const override = overrides[row.slug];
  if (override && override.trim()) return override;
  return row.label;
}

/**
 * Hook unificado de colunas do Kanban — padrão IGM.
 *
 * **Backend-first (2026-05-17):** dados vivem em `kanban_column_labels` +
 * `kanban_custom_columns` (SQLite) via `/api/v1/kanban/:workflow/columns`.
 *
 * Combina:
 *   - **Colunas padrão** (`DEAL_STAGES`, etc) imutáveis em código frontend
 *   - **Colunas custom** criadas pelo admin via `addColumn` (backend gera slug)
 *
 * API pública mantida idêntica:
 *   - `columns`, `customColumns`, `customLabels`, `getLabel`, `setLabel`,
 *     `resetLabel`, `addColumn`, `removeColumn`, `isDefault`, `isCustom`,
 *     `atSoftLimit`, `SOFT_LIMIT`
 *
 * Migration única é disparada via `useColumnLabels` (Query compartilhada).
 */

export type ColumnVariant = 'success' | 'warning' | 'danger' | 'neutral' | 'info';

/** FU-4 (fluxo unificado): situação canônica que uma coluna pode representar. */
export type SituacaoMapeada = 'a_cotar' | 'em_cotacao' | 'comprado' | 'recebido' | 'entregue';

function normalizeSituacao(v: string | null | undefined): SituacaoMapeada | null {
  const valid: readonly string[] = ['a_cotar', 'em_cotacao', 'comprado', 'recebido', 'entregue'];
  return v && valid.includes(v) ? (v as SituacaoMapeada) : null;
}

export interface CustomColumn {
  slug: string;
  label: string;
  ordem: number;
  variant: ColumnVariant;
  criadoEmIso: string;
  /** BUG-V2-020/037 — informativo (admin tem poder de excluir tudo igual). */
  isSeed?: boolean;
  /** FU-4: situação canônica mapeada (null = nenhuma). */
  situacaoMapeada?: SituacaoMapeada | null;
}

export interface KanbanColumn {
  slug: string;
  label: string;
  /** FU-4: "esta coluna representa:" — null = nenhuma (organizacional). */
  situacaoMapeada?: SituacaoMapeada | null;
  /**
   * BUG-V2-020/037 (2026-05-24): `isDefault` agora indica APENAS "vem do
   * fallback code-side" (deploy pré-Migration 045 ou backend offline). Pós-
   * migration, todas as colunas vêm do banco como `customColumn`, e o
   * frontend trata todas iguais (admin pode excluir qualquer uma).
   */
  isDefault: boolean;
  variant: ColumnVariant;
  ordem: number;
  isSeed?: boolean;
}

export interface DefaultStage {
  slug: string;
  label: string;
  ordem: number;
  variant: ColumnVariant;
}

const VALID_VARIANTS: ReadonlyArray<ColumnVariant> = [
  'success',
  'warning',
  'danger',
  'neutral',
  'info',
];

function normalizeVariant(v: string | undefined): ColumnVariant {
  if (v && (VALID_VARIANTS as readonly string[]).includes(v)) return v as ColumnVariant;
  return 'neutral';
}

const MAX_COLUMNS_SOFT_LIMIT = 12;

// ─── Slug helpers (mantidos por compat com chamadores e tests) ────────

export function slugifyColumnLabel(label: string): string {
  return label
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // remove diacriticos
    .toLowerCase()
    .replace(/[^a-z0-9\s_-]/g, '')
    .trim()
    .replace(/[\s-]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export function uniqueSlug(base: string, taken: Set<string>): string {
  if (!taken.has(base)) return base;
  let i = 2;
  while (taken.has(`${base}_${i}`)) i += 1;
  return `${base}_${i}`;
}

// ─── Hook ──────────────────────────────────────────────────────────────

/**
 * BUG-V2-020/037 (Migration 045): `defaults` agora é OPCIONAL — todas as
 * colunas vivem no banco (`kanban_custom_columns`) com `is_seed=1` pros
 * defaults DEAL_STAGES. `defaults` segue aceito como fallback defensivo
 * pra cenários (a) deploy pré-Migration 045 e (b) backend temporariamente
 * indisponível. Quando ambos defaults E backend retornam um mesmo slug, o
 * registro do BANCO PRECEDE (single source of truth pós-migration).
 */
export function useKanbanColumns(workflowId: string, defaults: DefaultStage[] = []) {
  const qc = useQueryClient();

  const defaultLabelMap = useMemo<Record<string, string>>(
    () => defaults.reduce<Record<string, string>>((acc, d) => ({ ...acc, [d.slug]: d.label }), {}),
    [defaults]
  );

  const { getLabel, setLabel, resetLabel, customLabels } = useColumnLabels(
    workflowId,
    defaultLabelMap
  );

  // Dispara o fetch único (mesma queryKey de useColumnLabels — Query dedup)
  const { data } = _useKanbanColumnsQuery(workflowId);
  const customRows = data?.customColumns ?? _readCustomColumns(workflowId);

  // ─── Lista combinada ────────────────────────────────────────────────

  const columns = useMemo<KanbanColumn[]>(() => {
    const activeCustom = [...customRows]
      .filter((c) => c.ativo)
      .sort((a, b) => a.ordem - b.ordem);
    const customSlugs = new Set(activeCustom.map((c) => c.slug));

    // Defaults code-side: só aparecem quando o slug NÃO existe no banco
    // (dedup contra duplicação se ambos os layers tiverem o slug).
    const fromDefaults: KanbanColumn[] = defaults
      .filter((d) => !customSlugs.has(d.slug))
      .map((d) => ({
        slug: d.slug,
        label: getLabel(d.slug),
        isDefault: true,
        variant: d.variant,
        ordem: d.ordem,
      }));

    // Para colunas vindas do banco, usa variant do próprio row (Migration 045
    // popula seeds com variant correto; admin custom = 'neutral').
    // BUG-V2-020/037: precedência de label é override > row.label (NÃO cai pra
    // defaults code-side mesmo se slug coincidir — banco é fonte de verdade).
    const overrides = _readLabelOverrides(workflowId);
    const fromCustom: KanbanColumn[] = activeCustom.map((c, idx) => ({
      slug: c.slug,
      label: labelForBackendRow(overrides, c),
      isDefault: false,
      variant: normalizeVariant(c.variant),
      ordem: defaults.length + idx + 1,
      isSeed: c.isSeed,
      situacaoMapeada: normalizeSituacao(c.situacaoMapeada),
    }));
    return [...fromDefaults, ...fromCustom];
  }, [defaults, customRows, getLabel, workflowId]);

  // Shape compat: customColumns mantém o shape antigo do hook
  const customColumns = useMemo<CustomColumn[]>(
    () =>
      customRows
        .filter((c) => c.ativo)
        .map((c) => ({
          slug: c.slug,
          label: c.label,
          ordem: c.ordem,
          variant: normalizeVariant(c.variant),
          criadoEmIso: c.criadoEmIso,
          isSeed: c.isSeed,
          situacaoMapeada: normalizeSituacao(c.situacaoMapeada),
        })),
    [customRows]
  );

  /**
   * BUG-V2-020/037: `isDefault` ainda existe pra compat, mas o "padrão" agora
   * é APENAS sobre origem (fallback code-side). Não bloqueia mais ações: admin
   * pode excluir qualquer coluna (do banco) — só colunas code-side que ainda
   * não foram seedadas no banco retornam true aqui, e essas são raras pós-
   * Migration 045 (deploy intermediário/backend down).
   */
  const isDefault = useCallback(
    (slug: string) => {
      const inCustom = customRows.some((c) => c.slug === slug && c.ativo);
      if (inCustom) return false; // banco sempre precede
      return defaults.some((d) => d.slug === slug);
    },
    [defaults, customRows]
  );

  const isCustom = useCallback(
    (slug: string) => customRows.some((c) => c.slug === slug && c.ativo),
    [customRows]
  );

  // ─── Mutations (backend) ────────────────────────────────────────────

  const invalidate = useCallback(
    () => qc.invalidateQueries({ queryKey: ['v1', 'kanban', workflowId, 'columns'] }),
    [qc, workflowId]
  );

  const addColumn = useCallback(
    async (input: { label: string }): Promise<CustomColumn> => {
      const trimmed = input.label.trim();
      if (trimmed.length < 2) throw new Error('Label da coluna precisa de pelo menos 2 caracteres');
      if (trimmed.length > 30) throw new Error('Label da coluna até 30 caracteres');
      const base = slugifyColumnLabel(trimmed);
      if (!base) throw new Error('Label inválido pra gerar slug');

      const row = await api.post<{
        slug: string;
        label: string;
        ordem: number;
        ativo: boolean;
        criadoPor: string | null;
        criadoEmIso: string;
      }>(`/api/v1/kanban/${workflowId}/columns`, { label: trimmed });

      // Otimismo: já mete no cache pra render imediato
      _customColumnsCache.set(workflowId, [..._readCustomColumns(workflowId), row]);
      await invalidate();
      return {
        slug: row.slug,
        label: row.label,
        ordem: row.ordem,
        variant: 'neutral',
        criadoEmIso: row.criadoEmIso,
      };
    },
    [workflowId, invalidate]
  );

  const removeColumn = useCallback(
    async (slug: string): Promise<void> => {
      // BUG-V2-020/037: removida guarda `isDefault`. Pós-Migration 045 todas
      // as colunas vivem no banco e são deletáveis pelo admin. Caso transitório
      // (deploy intermediário): backend retorna 404 e o caller propaga o erro
      // pra UI mostrar mensagem.
      await api.delete(`/api/v1/kanban/${workflowId}/columns/${slug}`);
      // Otimismo: remove do cache + renumera
      const remaining = _readCustomColumns(workflowId)
        .filter((c) => c.slug !== slug)
        .map((c, idx) => ({ ...c, ordem: idx + 1 }));
      _customColumnsCache.set(workflowId, remaining);
      await invalidate();
      // Limpa label override do slug removido (caller assume reset)
      await resetLabel(slug);
    },
    [workflowId, invalidate, resetLabel]
  );

  // FU-4: "esta coluna representa:" — mapeia (ou desmapeia) a situação canônica.
  const setColumnSituacao = useCallback(
    async (slug: string, situacao: SituacaoMapeada | null): Promise<void> => {
      await api.put(`/api/v1/kanban/${workflowId}/columns/${slug}/situacao`, { situacao });
      await invalidate();
    },
    [workflowId, invalidate],
  );

  const atSoftLimit = columns.length >= MAX_COLUMNS_SOFT_LIMIT;

  return {
    columns,
    customColumns,
    customLabels,
    getLabel,
    setLabel,
    resetLabel,
    addColumn,
    removeColumn,
    setColumnSituacao,
    isDefault,
    isCustom,
    atSoftLimit,
    SOFT_LIMIT: MAX_COLUMNS_SOFT_LIMIT,
  };
}

// ─── Test helpers ──────────────────────────────────────────────────────

/** SÓ tests — popula cache de custom columns. */
export function __seedCustomColumnsForTests(
  workflowId: string,
  customs: Array<{
    slug: string;
    label: string;
    ordem: number;
    ativo?: boolean;
    criadoPor?: string | null;
    criadoEmIso?: string;
    variant?: string;
    isSeed?: boolean;
  }>
): void {
  _customColumnsCache.set(
    workflowId,
    customs.map((c) => ({
      slug: c.slug,
      label: c.label,
      ordem: c.ordem,
      ativo: c.ativo ?? true,
      criadoPor: c.criadoPor ?? null,
      criadoEmIso: c.criadoEmIso ?? new Date().toISOString(),
      variant: c.variant ?? 'neutral',
      isSeed: c.isSeed,
    }))
  );
}
